import { useState } from 'react'
import type { FC } from 'react'
import { Box, Button, Typography } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import AttachmentIcon from '@mui/icons-material/AttachFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { useDropzone } from 'react-dropzone'
import { acceptMimeTypes } from '~/shared/lib/dropzoneAccept'
import { FFModal } from '~/shared/ui'
import IMG from './fileUploadPlaceholder.png'
import { red } from '@mui/material/colors'
import { visuallyHidden } from '@mui/utils'

// Separate types to distinguish between endpoints upstream
export interface FileWithType {
  data: File
  type: 'image' | 'document'
}

interface FileUploadProps {
  disabled: boolean
  file: FileWithType | null
  supportedImageTypes: Array<string>
  supportedDocTypes: Array<string>
  supportMessage: string
  handleFile: (file: FileWithType) => void
  handleClear?: () => void
}

const maxFileNameLength = 63

const FileUpload: FC<FileUploadProps> = props => {
  const [showModal, setShowModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<FileWithType | null>(null)

  const { getRootProps, fileRejections, isFocused, isDragAccept, isDragReject, getInputProps } =
    useDropzone({
      accept: acceptMimeTypes(props.supportedImageTypes.concat(props.supportedDocTypes)),
      maxFiles: 1,
      maxSize: 1e6 * 100, // 100MB upload max
      multiple: false,
      validator: file => {
        if (file.name.length > maxFileNameLength) {
          setPendingFile(null)
          return {
            code: 'name-too-large',
            message: `Ensure the filename has no more than ${maxFileNameLength} characters`,
          }
        }
        return null
      },
      onDropAccepted: ([dropped]) => {
        if (!dropped) return
        setPendingFile({
          data: dropped,
          type: props.supportedImageTypes.includes(dropped.type) ? 'image' : 'document',
        })
      },
    })
  return (
    <>
      <FFModal
        open={showModal}
        footer={
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              onClick={() => {
                setPendingFile(null)
                setShowModal(false)
                props.handleClear?.()
              }}
              color="secondary"
            >
              Cancel
            </Button>
            <Box sx={{ ml: 1 }}>
              <Button
                disabled={!pendingFile}
                variant="contained"
                color="primary"
                onClick={() => {
                  if (pendingFile) props.handleFile(pendingFile)
                  setPendingFile(null)
                  setShowModal(false)
                }}
              >
                Add
              </Button>
            </Box>
          </Box>
        }
      >
        <div {...getRootProps()}>
          <Box sx={{ p: 5 }}>
            <Box
              sx={{
                border: '0.3rem dashed',
                borderRadius: '4px',
                borderColor: isDragReject
                  ? red[500]
                  : `grey.${isFocused || isDragAccept ? 700 : 400}`,
                py: 10,
                px: 5,
              }}
            >
              <Typography align="center">
                <img width={220} src={IMG} alt="Upload file" />
                <br />
                Drop your file here or{' '}
                <Typography component="span" sx={{ color: 'primary.main' }}>
                  browse
                </Typography>
                <br />
                {props.supportMessage}
              </Typography>
            </Box>
            {pendingFile ? (
              <Box sx={{ mt: 2, bgcolor: 'grey.100', borderRadius: '5px', p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InsertDriveFileIcon color="secondary" />
                  <Box sx={{ ml: 1, overflow: 'hidden' }}>
                    <Typography style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pendingFile.data.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : null}
            {fileRejections.length ? (
              <Box sx={{ mt: 2, bgcolor: 'grey.100', borderRadius: '5px', p: 1 }}>
                {fileRejections.map(fileRejection => (
                  <Box key={fileRejection.file.name} sx={{ ml: 1, overflow: 'hidden' }}>
                    {fileRejection.errors.map(e => (
                      <Typography
                        key={e.code}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: red[500] }}
                      >
                        {e.message}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </div>
      </FFModal>
      <div style={visuallyHidden}>
        <Typography>
          <input {...getInputProps()} />
        </Typography>
      </div>
      <IconButton
        disabled={props.disabled || !!props.file}
        sx={{ my: -1 }}
        onClick={() => setShowModal(true)}
        size="large"
      >
        <AttachmentIcon />
      </IconButton>
    </>
  )
}

export default FileUpload
